import { DAYS, HOURS, fmtHour } from "../lib/schedule";

/**
 * Schedule grid (24 hours x 7 days) for a single site.
 * - shifts: map of `${day}|${hour}` -> {site_id, day, hour, worker_id}
 * - workersById: map of worker_id -> worker
 */
export default function ScheduleGrid({
  shifts,
  workersById,
  onCellClick,
  readOnly = false,
  colorBySite = false,
  sitesById = {},
  conflictKeys = null, // Set of `${worker_id}|${day}|${hour}` strings
}) {
  return (
    <div className="border border-gray-300 bg-white overflow-auto rounded-sm" data-testid="schedule-grid">
      <div
        className="grid min-w-[860px]"
        style={{ gridTemplateColumns: "72px repeat(7, minmax(100px, 1fr))" }}
      >
        <div className="sticky top-0 left-0 z-20 bg-gray-100 border-b border-r border-gray-300 px-2 py-3 text-[10px] uppercase tracking-widest text-gray-500 font-bold">
          Hour
        </div>
        {DAYS.map((d, i) => (
          <div
            key={d}
            className="sticky top-0 z-10 bg-gray-100 border-b border-gray-300 px-3 py-3 text-xs uppercase tracking-widest font-bold text-gray-900 text-center"
            data-testid={`schedule-day-${i}`}
          >
            {d}
          </div>
        ))}

        {HOURS.map((h) => (
          <ScheduleRow
            key={h}
            hour={h}
            shifts={shifts}
            workersById={workersById}
            onCellClick={onCellClick}
            readOnly={readOnly}
            colorBySite={colorBySite}
            sitesById={sitesById}
            conflictKeys={conflictKeys}
          />
        ))}
      </div>
    </div>
  );
}

function ScheduleRow({ hour, shifts, workersById, onCellClick, readOnly, colorBySite, sitesById, conflictKeys }) {
  return (
    <>
      <div className="sticky left-0 z-10 bg-gray-50 border-r border-b border-gray-200 px-2 py-2 font-mono text-xs text-gray-600 flex items-center justify-end">
        {fmtHour(hour)}
      </div>
      {DAYS.map((_, day) => (
        <ScheduleCell
          key={`${day}-${hour}`}
          day={day}
          hour={hour}
          shift={shifts[`${day}|${hour}`]}
          workersById={workersById}
          sitesById={sitesById}
          colorBySite={colorBySite}
          readOnly={readOnly}
          onCellClick={onCellClick}
          conflictKeys={conflictKeys}
        />
      ))}
    </>
  );
}

function ScheduleCell({ day, hour, shift, workersById, sitesById, colorBySite, readOnly, onCellClick, conflictKeys }) {
  const workerId = shift?.worker_id;
  const siteId = shift?.site_id;
  const worker = workerId ? workersById[workerId] : null;
  const site = colorBySite && siteId ? sitesById[siteId] : null;
  const color = site?.color || worker?.color;

  const inConflict =
    conflictKeys && workerId
      ? conflictKeys.has(`${workerId}|${day}|${hour}`)
      : false;

  const style = color
    ? { background: color.bg, color: color.text, borderLeft: `3px solid ${color.border}` }
    : { background: "#fff" };
  if (inConflict) {
    style.boxShadow = "inset 0 0 0 2px #b91c1c";
    style.background = "#fee2e2";
    style.color = "#7f1d1d";
  }

  const label = colorBySite ? site?.name : worker?.name || "";

  return (
    <button
      type="button"
      disabled={readOnly}
      onClick={() => !readOnly && onCellClick && onCellClick(day, hour)}
      data-testid={`schedule-cell-${day}-${hour}${inConflict ? "-conflict" : ""}`}
      className={`border-b border-r border-gray-200 px-2 py-2 text-left text-[11px] font-medium leading-tight min-h-[44px] hover:outline hover:outline-2 hover:outline-[#002FA7] transition ${
        readOnly ? "cursor-default" : "cursor-pointer"
      }`}
      style={style}
      title={inConflict ? `${label} · CONFLICT — double-booked` : (label || "Unassigned")}
    >
      {label ? (
        <span className="truncate block">{label}</span>
      ) : (
        <span className="text-gray-300 text-xs">—</span>
      )}
      {inConflict && (
        <span className="block text-[9px] font-bold uppercase tracking-widest text-red-800 mt-0.5">
          conflict
        </span>
      )}
    </button>
  );
}
